import React, { useState, useEffect } from 'react';
import { Archive } from 'lucide-react';
import Card from '../../components/Card';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, isEmptyCell } from '../../utils/helpers';

const CabinetPlacementsView = () => {
  const { user } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const { error: showError } = useToast();

  useEffect(() => {
    if (!user?.id) return;
    loadPlacements();
  }, [user?.id]);

  const renderQty = (data) => (isEmptyCell(data?.quantity) ? renderCell(data?.quantity) : data.quantity);
  const renderStatus = (data) => (data?.status ? data.status : renderCell(null));

  const loadPlacements = async () => {
    try {
      setLoading(true);
      // Fetch only data for this client
      const data = await apiService.getCabinetPlacements(user?.id);
      setPlacements(data);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Archive className="w-8 h-8 text-red-600" />
          Emplacement des Armoires
        </h1>
        <p className="text-gray-600 mt-1">
          Consultation des emplacements des accessoires dans les armoires
        </p>
      </div>

      {/* Table Structure - Read Only */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  Accessoires
                </th>
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  Bibliothèque DOE
                </th>
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  Pomperie DOE
                </th>
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  Parking DDO
                </th>
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  Parking coté IT DDO
                </th>
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200">
                  164 Studios
                </th>
                <th colSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Magasin
                </th>
              </tr>
              <tr className="bg-gray-100">
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200">État</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200">État</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200">État</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200">État</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 border-r border-gray-200">État</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">État</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {['Fléxible 70', 'Fléxible 45', 'Lance 70', 'Lance 45', 'Réduction 70/45', 'Réduction 100/70', 'Division 70', 'Division 45', 'Tricoise'].map((accessoire) => {
                // Get data for this accessoire across all locations
                const biblioData = placements.find(p => p.accessoryType === accessoire && p.location === 'Bibliothèque DOE');
                const pomperieData = placements.find(p => p.accessoryType === accessoire && p.location === 'Pomperie DOE');
                const parkingDDOData = placements.find(p => p.accessoryType === accessoire && p.location === 'Parking DDO');
                const parkingITData = placements.find(p => p.accessoryType === accessoire && p.location === 'Parking coté IT DDO');
                const studiosData = placements.find(p => p.accessoryType === accessoire && p.location === '164 Studios');
                const magasinData = placements.find(p => p.accessoryType === accessoire && p.location === 'Magasin');
                
                return (
                  <tr key={accessoire} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200">{accessoire}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(biblioData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-600 border-r border-gray-200">{renderStatus(biblioData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(pomperieData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-600 border-r border-gray-200">{renderStatus(pomperieData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(parkingDDOData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-600 border-r border-gray-200">{renderStatus(parkingDDOData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(parkingITData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-600 border-r border-gray-200">{renderStatus(parkingITData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(studiosData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-600 border-r border-gray-200">{renderStatus(studiosData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(magasinData)}</td>
                    <td className="px-2 py-3 text-sm text-center text-gray-600">{renderStatus(magasinData)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {placements.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucune donnée disponible
            </h3>
            <p className="text-gray-600">
              Aucun emplacement d'armoire n'a encore été configuré
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CabinetPlacementsView;

